import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, BellRing, BookOpen, Cloud, Code2, Database, Flame, GraduationCap, Layers, LayoutDashboard, ListChecks,
  Newspaper, NotebookPen, Play, ShieldCheck, Target, Trophy,
} from 'lucide-react';
import { apiErrorMessage, courseService, dashboardService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import { getCourseColor, getPhaseColor } from '../theme/phases';
import { Badge, Button, EmptyState, GlobalShell, IconSquare, Loader, PageHeader } from '../components/ui';
import './Courses.css';

const COURSE_ICONS = { school: GraduationCap, cloud: Cloud, code: Code2, security: ShieldCheck, data: Database };
const WEEK_GOALS = [['lessons', 'Aulas', BookOpen], ['questions', 'Questões', ListChecks], ['flashcards', 'Flashcards', Layers]];

/**
 * Cursos liberados, no formato de Trilhas e Artigos: "continue de onde parou", números da semana,
 * um card por curso (fases, simulados, flashcards, erros, nível e lembretes) e a lateral com metas e atividade.
 */
function Courses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectCourse } = useCourse();
  const [courses, setCourses] = useState(null);
  const [dash, setDash] = useState({});
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    courseService.overview()
      .then(list => {
        setCourses(list);
        // O painel de cada curso traz simulados, flashcards, erros, nível e a atividade
        Promise.all(list.map(c => dashboardService.getDashboard(c.slug).then(d => [c.slug, d]).catch(() => [c.slug, null])))
          .then(entries => setDash(Object.fromEntries(entries)));
      })
      .catch(err => setError(apiErrorMessage(err, 'Não foi possível carregar os cursos.')));
  };
  useEffect(load, []);

  const open = async (course, path) => {
    await selectCourse(course.slug);
    navigate(`/curso/${course.slug}${path}`);
  };

  const articlesOnly = user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN' && !user.allCourses && user.courseIds?.length === 0;
  const dashes = Object.values(dash).filter(Boolean);
  const activity = dashes[0]?.activity; // atividade e metas são da conta, iguais em todos os cursos
  const goals = dashes[0]?.goals;
  const total = courses?.reduce((s, c) => s + c.lessons, 0) || 0;
  const done = courses?.reduce((s, c) => s + c.done, 0) || 0;
  const due = dashes.reduce((s, d) => s + (d.dueFlashcards || 0), 0);
  const errors = dashes.reduce((s, d) => s + (d.totalErrors || 0), 0);

  // Última aula vista entre todos os cursos
  const resume = useMemo(() => {
    if (!courses) return null;
    return courses.filter(c => c.lastLesson).sort((a, b) => String(b.lastLesson.viewedAt).localeCompare(String(a.lastLesson.viewedAt)))[0] || null;
  }, [courses]);

  return (
    <GlobalShell>
      <PageHeader
        icon={GraduationCap}
        eyebrow="Para estudar"
        title="Cursos"
        subtitle="Aulas, simulados e flashcards de cada curso liberado para você, organizados por fase."
      />

      {error && <EmptyState file="cursos.xml" title="Não foi possível carregar os cursos" description={error} action={<Button onClick={load}>Tentar de novo</Button>} />}
      {!error && !courses && <Loader label="Carregando cursos..." />}
      {courses?.length === 0 && (
        <EmptyState
          file="cursos.xml"
          title={articlesOnly ? 'Sua conta é só de artigos' : 'Nenhum curso disponível'}
          description={articlesOnly
            ? 'Você lê, curte e comenta os artigos da turma. Para estudar um curso, peça ao administrador.'
            : 'Não há cursos cadastrados no momento.'}
          action={articlesOnly && <Button icon={Newspaper} onClick={() => navigate('/artigos')}>Ver artigos</Button>}
        />
      )}

      {courses?.length > 0 && (
        <>
          {resume && (
            <section className="crs-resume" data-tour="curso-continuar">
              <span className="crs-resume__icon"><Play size={22} aria-hidden="true" /></span>
              <span className="crs-resume__text">
                <span className="crs-resume__eyebrow">Continue de onde parou · {resume.name}</span>
                <strong>{resume.lastLesson.name}</strong>
                <span>{resume.lastLesson.topicName}</span>
              </span>
              <Button icon={Play} onClick={() => open(resume, `/estudo/${resume.lastLesson.topicId}/aula/${resume.lastLesson.lessonId}`)}>Continuar aula</Button>
            </section>
          )}

          <div className="crs-stats">
            <Stat icon={BookOpen} value={done} unit={`de ${total}`} label="Aulas concluídas" hint={`${total ? Math.round((done / total) * 100) : 0}% dos cursos`} />
            <Stat icon={Flame} value={activity?.streak ?? '—'} unit={activity?.streak === 1 ? 'dia' : 'dias'} label="Sequência" hint={activity ? `recorde de ${activity.longestStreak} · ${activity.studiedToday ? 'estudou hoje' : 'ainda não estudou hoje'}` : ''} accent={activity?.studiedToday} />
            <Stat icon={Layers} value={due} label="Flashcards para hoje" hint={due ? 'revisão espaçada' : 'nada para revisar'} />
            <Stat icon={NotebookPen} value={errors} label="Erros para revisar" hint="no caderno de erros" />
          </div>

          <div className="crs-layout">
            <div className="crs-cards" data-tour="cursos-grid">
              {courses.map((c, i) => (
                <CourseCard key={c.id} course={c} index={i} dash={dash[c.slug]} onOpen={(path) => open(c, path)} />
              ))}
            </div>

            <aside className="crs-side">
              <section className="crs-sidecard">
                <h2 className="crs-sidecard__title">Sua semana</h2>
                {WEEK_GOALS.map(([key, label, Icon]) => {
                  const value = activity?.week?.[key] || 0;
                  const goal = goals?.[key];
                  const pct = goal ? Math.min(100, Math.round((value / goal) * 100)) : 0;
                  return (
                    <div key={key} className="crs-goal">
                      <span className="crs-goal__head">
                        <span><Icon size={15} aria-hidden="true" />{label}</span>
                        <strong>{value}{goal ? <span> / {goal}</span> : null}</strong>
                      </span>
                      {goal ? <span className="crs-goal__bar"><span style={{ width: `${pct}%` }} /></span> : null}
                    </div>
                  );
                })}
                {!goals?.lessons && !goals?.questions && !goals?.flashcards && (
                  <p className="crs-sidecard__note">Sem metas semanais. <button type="button" onClick={() => navigate('/conta')}>Definir metas</button></p>
                )}
              </section>

              {activity?.lastDays?.length > 0 && (
                <section className="crs-sidecard">
                  <h2 className="crs-sidecard__title">Últimos {activity.lastDays.length} dias</h2>
                  <div className="crs-heat" role="img" aria-label="Dias com estudo nas últimas semanas">
                    {activity.lastDays.map(d => (
                      <span key={d.date} title={`${d.date.split('-').reverse().join('/')} · ${d.total} atividades`}
                            className={`crs-heat__d crs-heat__d--${d.total === 0 ? 0 : d.total < 10 ? 1 : d.total < 30 ? 2 : 3}`} />
                    ))}
                  </div>
                  <span className="crs-heat__legend">Menos<i className="crs-heat__d--0" /><i className="crs-heat__d--1" /><i className="crs-heat__d--2" /><i className="crs-heat__d--3" />Mais</span>
                </section>
              )}

              <section className="crs-sidecard crs-sidecard--tint">
                <h2 className="crs-sidecard__title">Também por aqui</h2>
                <button type="button" className="crs-link" onClick={() => navigate('/trilhas')}>
                  <Target size={17} aria-hidden="true" /><span><strong>Trilhas</strong>Um caminho com plano diário até a prova.</span><ArrowRight size={16} aria-hidden="true" />
                </button>
                <button type="button" className="crs-link" onClick={() => navigate('/artigos')}>
                  <Newspaper size={17} aria-hidden="true" /><span><strong>Artigos</strong>A leitura colaborativa do time.</span><ArrowRight size={16} aria-hidden="true" />
                </button>
              </section>
            </aside>
          </div>
        </>
      )}
    </GlobalShell>
  );
}

function Stat({ icon: Icon, value, unit, label, hint, accent }) {
  return (
    <div className="crs-stat">
      <span className="crs-stat__icon"><Icon size={18} aria-hidden="true" /></span>
      <span className="crs-stat__value"><strong className={accent ? 'is-accent' : ''}>{value}</strong>{unit && <span>{unit}</span>}</span>
      <span className="crs-stat__label">{label}</span>
      {hint && <span className="crs-stat__hint">{hint}</span>}
    </div>
  );
}

function CourseCard({ course: c, index, dash, onOpen }) {
  const topics = c.phases.reduce((s, p) => s + (p.topics || 0), 0);
  const g = dash?.gamification;
  const reminder = dash?.reminders?.[0];
  const reminderPath = reminder?.type === 'exam' ? `/exam/${reminder.refId}`
    : reminder?.type === 'flashcards' ? '/flashcards' : reminder?.type === 'errors' ? '/erros' : null;
  return (
    <article className="crs-card" data-tour="curso-card">
      <div className="crs-card__top">
        <IconSquare icon={COURSE_ICONS[c.icon] || BookOpen} size={48} color={getCourseColor(index)} />
        <span className="crs-card__badges">
          {g && (
            <button type="button" className="crs-level" onClick={() => onOpen('/conquistas')} title="Ver conquistas">
              <Trophy size={13} aria-hidden="true" />Nível {g.level} · {g.xp.toLocaleString('pt-BR')} XP
            </button>
          )}
          <Badge tone="navy">{c.phases.length || c.phasesCount} fases</Badge>
        </span>
      </div>
      <button type="button" className="crs-card__title" onClick={() => onOpen('')}>{c.name}</button>
      {c.description && <p className="crs-card__desc">{c.description}</p>}

      <div className="crs-card__stats">
        <span><strong>{c.pct}%</strong>{c.done} de {c.lessons} aulas</span>
        <span><strong>{topics}</strong>tópicos</span>
        <span><strong>{dash?.completedExams ?? '—'}</strong>{dash?.averageScore != null ? `simulados · média ${Math.round(dash.averageScore)}%` : 'simulados'}</span>
        <span><strong>{dash?.dueFlashcards ?? '—'}</strong>flashcards hoje</span>
      </div>

      <ul className="crs-phases">
        {c.phases.map(p => {
          const pct = p.lessons ? Math.round((p.done / p.lessons) * 100) : 0;
          return (
            <li key={p.number}>
              <span className="crs-phases__num" style={{ background: getPhaseColor(p.number) }}>{p.number}</span>
              <span className="crs-phases__name">{p.name || `Fase ${p.number}`}</span>
              <span className="crs-phases__bar"><span style={{ width: `${pct}%`, background: getPhaseColor(p.number) }} /></span>
              <span className="crs-phases__count">{p.done}/{p.lessons}</span>
            </li>
          );
        })}
      </ul>

      {reminder && (
        <div className={`crs-reminder crs-reminder--${reminder.type}`}>
          <BellRing size={16} aria-hidden="true" />
          <span>{reminder.message}</span>
          {reminderPath && reminder.action && <button type="button" onClick={() => onOpen(reminderPath)}>{reminder.action}<ArrowRight size={14} aria-hidden="true" /></button>}
        </div>
      )}

      <div className="crs-card__foot">
        <span className="crs-card__last">
          {c.lastLesson
            ? <>Você parou em <strong>{c.lastLesson.name}</strong> · {c.lastLesson.topicName}</>
            : 'Comece pela primeira fase.'}
        </span>
        <span className="crs-card__actions">
          <Button variant="ghost" icon={LayoutDashboard} onClick={() => onOpen('')}>Visão geral</Button>
          <Button variant="outline" icon={Target} onClick={() => onOpen('/simulados')}>Simulados</Button>
          {c.lastLesson
            ? <Button icon={Play} onClick={() => onOpen(`/estudo/${c.lastLesson.topicId}/aula/${c.lastLesson.lessonId}`)}>Continuar</Button>
            : <Button icon={ArrowRight} onClick={() => onOpen('/estudo')}>Começar</Button>}
        </span>
      </div>
    </article>
  );
}

export default Courses;
